import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { matchPath } from "react-router";
import { Serializer } from "../persistence";
import { fetchProductPlanAsPlanById } from "src/utils/plans";
import { AppDispatch, GetAppState } from "../store";
import { Plan } from "./plans";
import { clearPromoCode } from "./promoCode";
import invariant from "tiny-invariant";
import { removeURLParam } from "src/utils/urlParams";

const forcedPlanId = new URLSearchParams(window.location.search)
  .get("noom_plan_id")
  ?.trim?.();

// Maps to all of the pre-LBF payment funnel paths. This
// only needs to be maintained while we still support those paths.
const pathsWithPlans = [
  "/purchase/(en|es|de)/:planId",
  "/purchase/:planId",
  "/purchase-hm/:planId",
];

function extractPlanFromPath(pathname: string) {
  for (let i = 0; i < pathsWithPlans.length; i++) {
    const match = matchPath<{ planId: string }>(pathname, {
      path: pathsWithPlans[i],
    });
    if (match?.params) {
      return match.params.planId;
    }
  }
  return undefined;
}

export type RecommendedPlanState = Partial<Plan> & {
  isForcedPlan?: boolean;

  /**
   * Provides the most recent trial fee selected by the user.
   * Note that these selections can be indirect i.e. promo code is applied.
   */
  userSelectedTrialFee?: number;
};

export const serialize: Serializer<"recommendedPlan"> = {
  storage: "local",
  load(storedValue: RecommendedPlanState): RecommendedPlanState {
    if (forcedPlanId) {
      // Note that we may want to remove the noom_plan_id query parameter in
      // the future, but for now it needs to remain to support rerolls into
      // older code.
      return {
        noom_plan_id: forcedPlanId,
        isForcedPlan: true,
        userSelectedTrialFee: storedValue?.userSelectedTrialFee,
      };
    }

    // Seed plan allows the plan to be set via query parameter, without
    // making it be a forced plan. This is generally used for testing.
    const seedPlanId = new URLSearchParams(window.location.search).get(
      "seed_plan_id"
    );
    if (seedPlanId) {
      removeURLParam("seed_plan_id");
      return {
        noom_plan_id: seedPlanId,
        isForcedPlan: false,
        userSelectedTrialFee: storedValue?.userSelectedTrialFee,
      };
    }

    const pathPlan = extractPlanFromPath(window.location.pathname);
    if (pathPlan) {
      return {
        noom_plan_id: pathPlan,
        isForcedPlan: false,
        userSelectedTrialFee: storedValue?.userSelectedTrialFee,
      };
    }

    return storedValue;
  },
  save(plan: RecommendedPlanState) {
    // WARN: Remember backwards compatibility when changing this.
    const { noom_plan_id, isForcedPlan, userSelectedTrialFee } = plan;
    return { noom_plan_id, isForcedPlan, userSelectedTrialFee };
  },
};

export function planHasCustomTrialPrice(plan: RecommendedPlanState) {
  return plan.userSelectedTrialFee != null;
}

function getTrialFee(userSelectedTrialFee, payloadTrialFee) {
  return userSelectedTrialFee ?? payloadTrialFee ?? 0;
}

const recommendedPlansSlice = createSlice({
  name: "recommendedPlan",
  initialState: {
    noom_plan_id: forcedPlanId,
    isForcedPlan: !!forcedPlanId,
  } as RecommendedPlanState,
  reducers: {
    setRecommendedPlan: (state, action: PayloadAction<Plan>) => {
      return {
        ...action.payload,
        trial_fee: getTrialFee(
          state.userSelectedTrialFee,
          action.payload.trial_fee
        ),
        userSelectedTrialFee: state.userSelectedTrialFee,
        isForcedPlan: action.payload.noom_plan_id === forcedPlanId,
      };
    },
    setTrialFee: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        trial_fee: action.payload,
        userSelectedTrialFee: action.payload,
      };
    },
    updateRecommendedPlan: (
      state,
      action: PayloadAction<RecommendedPlanState>
    ) => {
      // Does have partial updates
      return {
        ...state,
        ...action.payload,
        trial_fee: getTrialFee(
          state.userSelectedTrialFee,
          action.payload.trial_fee
        ),
      };
    },
  },
});

export const { setTrialFee, updateRecommendedPlan } =
  recommendedPlansSlice.actions;

const { setRecommendedPlan } = recommendedPlansSlice.actions;

export default recommendedPlansSlice;

export function setRecommendPlanById(
  noomPlanId: string,
  { overwriteForced = false } = {}
) {
  invariant(noomPlanId, "noomPlanId is required");
  return async (dispatch: AppDispatch, getState: GetAppState) => {
    const { recommendedPlan: currentPlan } = getState();

    // Do not overwrite forced plan unless explicitly requested
    if (!overwriteForced && currentPlan?.isForcedPlan) {
      return;
    }

    // NOP if resetting to ourselves
    if (currentPlan?.noom_plan_id === noomPlanId) {
      return;
    }

    // If we are ejecting from a forced plan, pull the query param
    if (currentPlan?.isForcedPlan) {
      removeURLParam("noom_plan_id");
    }

    await dispatch(clearPromoCode());

    const recommendedPlan = await fetchProductPlanAsPlanById(noomPlanId);

    if (!recommendedPlan) {
      throw new Error(`No plan found with noom_plan_id: ${noomPlanId}`);
    }

    dispatch(setRecommendedPlan(recommendedPlan));
  };
}
